'use client';

import { useEffect, useRef, useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import {
  createCleanorExportFileName,
  formatFileSize,
  isPdfFile,
  rebuildPdfFromRenderedPages,
} from '@/lib/browser-pdf-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  readPdfSource,
  revokePdfSource,
  withPdfDocument,
  type PdfToolSource,
} from '@/components/tools/pdf-tool-shared';

export type UnlockRepairVariant = 'unlock-pdf' | 'repair-pdf';

const toolCopy: Record<
  UnlockRepairVariant,
  {
    title: string;
    subtitle: string;
    badge: string;
    buttonLabel: string;
    processingLabel: string;
    successLabel: string;
  }
> = {
  'unlock-pdf': {
    title: 'Unlock a password-protected PDF',
    subtitle:
      'Enter the correct password, rebuild the document locally, and export an unlocked copy.',
    badge: 'Unlock',
    buttonLabel: 'Unlock PDF',
    processingLabel: 'Unlocking…',
    successLabel: 'Unlocked PDF export',
  },
  'repair-pdf': {
    title: 'Repair a damaged PDF in the browser',
    subtitle:
      'Try a direct rebuild first, then fall back to page rendering when a mildly damaged PDF still has recoverable pages.',
    badge: 'Repair',
    buttonLabel: 'Repair PDF',
    processingLabel: 'Repairing…',
    successLabel: 'Repaired PDF export',
  },
};

type RepairSummary = {
  recoveredPages: number[];
  droppedPages: number[];
  usedRenderedFallback: boolean;
};

export default function BrowserUnlockRepairPdf({ variant }: { variant: UnlockRepairVariant }) {
  const copy = toolCopy[variant];
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [source, setSource] = useState<PdfToolSource | null>(null);
  const [password, setPassword] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [repairSummary, setRepairSummary] = useState<RepairSummary | null>(null);

  useEffect(() => () => revokePdfSource(source), [source]);

  function replaceSource(nextSource: PdfToolSource | null) {
    setSource((current) => {
      revokePdfSource(current);
      return nextSource;
    });
  }

  async function handleFileSelection(nextFile: File | null) {
    setFile(nextFile);
    replaceSource(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);
    setRepairSummary(null);

    if (!nextFile) {
      return;
    }

    if (!isPdfFile(nextFile)) {
      setFile(null);
      setErrorMessage('Choose one PDF file.');
      return;
    }

    if (variant === 'unlock-pdf') {
      return;
    }

    setIsLoading(true);
    try {
      replaceSource(await readPdfSource(nextFile));
    } catch {
      replaceSource(null);
    } finally {
      setIsLoading(false);
    }
  }

  function clearFile() {
    setFile(null);
    replaceSource(null);
    setPassword('');
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);
    setRepairSummary(null);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function runTool() {
    if (!file) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    if (variant === 'unlock-pdf' && !password.trim()) {
      setErrorMessage('Enter the PDF password first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setRepairSummary(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another PDF recovery export right now.',
      );

      let blob: Blob;
      let summary: RepairSummary | null = null;

      if (variant === 'unlock-pdf') {
        const rebuilt = await withPdfDocument(file, password, (document) =>
          rebuildPdfFromRenderedPages({
            pdfDocument: document,
            encryption: undefined,
          }),
        );
        blob = rebuilt.blob;
        summary = {
          recoveredPages: rebuilt.recoveredPages,
          droppedPages: rebuilt.droppedPages,
          usedRenderedFallback: true,
        };
      } else {
        try {
          const document = await PDFDocument.load(await file.arrayBuffer(), {
            ignoreEncryption: true,
          });
          blob = new Blob([(await document.save()) as unknown as BlobPart], {
            type: 'application/pdf',
          });
          summary = {
            recoveredPages: Array.from(
              { length: document.getPageCount() },
              (_, index) => index + 1,
            ),
            droppedPages: [],
            usedRenderedFallback: false,
          };
        } catch {
          const rebuilt = await withPdfDocument(file, undefined, (document) =>
            rebuildPdfFromRenderedPages({
              pdfDocument: document,
            }),
          );
          blob = rebuilt.blob;
          summary = {
            recoveredPages: rebuilt.recoveredPages,
            droppedPages: rebuilt.droppedPages,
            usedRenderedFallback: true,
          };
        }
      }

      const url = URL.createObjectURL(blob);
      downloadBlob(
        url,
        createCleanorExportFileName(
          variant,
          [file.name.replace(/\.pdf$/i, ''), summary?.recoveredPages.length || 0],
          'pdf',
        ),
      );
      setTimeout(() => URL.revokeObjectURL(url), 0);
      setResultSize(blob.size);
      setRepairSummary(summary);
      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : variant === 'unlock-pdf'
            ? 'The PDF could not be unlocked with this password.'
            : 'The PDF could not be repaired in the browser.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="project-detail-card tool-live-card">
      <div className="tool-live-heading">
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
        </div>
        <span className="tool-live-badge">{copy.badge}</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{file ? file.name : 'Choose one PDF or drop it here'}</strong>
              <span>
                {file
                  ? formatFileSize(file.size)
                  : variant === 'unlock-pdf'
                    ? 'Password-protected PDFs only'
                    : 'Best-effort beta for mildly damaged PDFs'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          {variant === 'unlock-pdf' ? (
            <label className="tool-live-field">
              <span>PDF password</span>
              <input
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter the opening password"
                type="password"
                value={password}
              />
            </label>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!file || isLoading || isProcessing}
              onClick={() => void runTool()}
              type="button"
            >
              {isProcessing ? copy.processingLabel : copy.buttonLabel}
            </button>
            {file ? (
              <button className="button button-ghost" onClick={clearFile} type="button">
                Clear
              </button>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Recovery summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Source</strong>
              <span>{file ? file.name : 'No PDF selected'}</span>
            </div>
            <div>
              <strong>Preview</strong>
              <span>
                {source
                  ? `${source.pageCount} readable pages`
                  : variant === 'repair-pdf' && file
                    ? 'Preview unavailable'
                    : 'Locked until export'}
              </span>
            </div>
            <div>
              <strong>Recovered</strong>
              <span>
                {repairSummary ? repairSummary.recoveredPages.length : 'Not exported yet'}
              </span>
            </div>
            <div>
              <strong>Result</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
          </div>
          {repairSummary ? (
            <div className="tool-live-panel-note">
              <strong>
                {repairSummary.usedRenderedFallback
                  ? 'Rendered rebuild used'
                  : 'Direct rebuild used'}
              </strong>
              <span>
                {repairSummary.droppedPages.length
                  ? `${repairSummary.droppedPages.length} pages could not be recovered.`
                  : 'All readable pages were included in the export.'}
              </span>
            </div>
          ) : (
            <div className="tool-live-panel-note">
              <strong>Flattening warning</strong>
              <span>
                Unlock and repair exports may flatten searchable text, forms, and annotations
                because the browser-first recovery path rebuilds the document.
              </span>
            </div>
          )}
        </aside>
      </div>

      {source?.previewUrl ? (
        <div className="tool-live-preview">
          <h3>Preview</h3>
          <img alt="PDF preview" className="tool-result-image" src={source.previewUrl} />
        </div>
      ) : null}
    </article>
  );
}

function downloadBlob(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}
